/**
 * Timeline Utility Functions
 *
 * Helper functions for the Sales Timeline feature (US-PE-006).
 * Handles date range calculations, week grouping, and yield curve defaults.
 */

import { createDate, toLocalDateString } from './dateUtils.js'

/**
 * Get the week bounds (Monday to Sunday) for a given date
 * @param {Date|string} date - Date to get week bounds for
 * @returns {Object} { start: Date, end: Date } - Monday and Sunday of the week
 */
export function getWeekBounds(date) {
  const d = typeof date === 'string' ? createDate(date) : createDate(date.getTime())

  // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = d.getDay()

  // Calculate days to subtract to get to Monday
  // If Sunday (0), go back 6 days; otherwise go back (dayOfWeek - 1) days
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1

  // Clone and set to Monday
  const monday = createDate(d.getTime())
  monday.setDate(d.getDate() - daysToMonday)
  monday.setHours(0, 0, 0, 0)

  // Clone and set to Sunday
  const sunday = createDate(monday.getTime())
  sunday.setDate(monday.getDate() + 6)
  sunday.setHours(23, 59, 59, 999)

  return { start: monday, end: sunday }
}

/**
 * Get the date range for a specific view mode
 * @param {Date|string} anchorDate - The anchor date (typically today)
 * @param {string} viewMode - One of '7d', '14d', 'month'
 * @returns {Object} { start: Date, end: Date, weekCount: number }
 */
export function getDateRangeForView(anchorDate, viewMode) {
  const anchor = typeof anchorDate === 'string' ? createDate(anchorDate) : createDate(anchorDate.getTime())
  anchor.setHours(0, 0, 0, 0)

  // Get the current week's Monday
  const { start: weekStart } = getWeekBounds(anchor)

  let start, end, weekCount

  switch (viewMode) {
    case '7d':
      // Single week: Monday to Sunday of current week
      start = createDate(weekStart.getTime())
      end = createDate(weekStart.getTime())
      end.setDate(end.getDate() + 6)
      end.setHours(23, 59, 59, 999)
      weekCount = 1
      break

    case '14d':
      // Two weeks: current week + next week (forward-looking)
      start = createDate(weekStart.getTime())
      end = createDate(weekStart.getTime())
      end.setDate(end.getDate() + 13) // Current week (7 days) + next week (7 days) - 1
      end.setHours(23, 59, 59, 999)
      weekCount = 2
      break

    case 'month':
    default: {
      // Show weeks that make up the current calendar month
      // Find first Monday on or before the 1st of the anchor's month
      const firstOfMonth = createDate(anchor.getTime())
      firstOfMonth.setDate(1)
      const { start: firstWeekStart } = getWeekBounds(firstOfMonth)

      // Find the last day of the month
      const lastOfMonth = createDate(anchor.getTime())
      lastOfMonth.setMonth(lastOfMonth.getMonth() + 1)
      lastOfMonth.setDate(0) // Last day of current month
      const { end: lastWeekEnd } = getWeekBounds(lastOfMonth)

      start = firstWeekStart
      end = lastWeekEnd

      // Calculate week count (typically 4-5 weeks)
      const diffTime = end.getTime() - start.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      weekCount = Math.ceil(diffDays / 7)
      break
    }
  }

  return { start, end, weekCount }
}

/**
 * Group daily data into week-based arrays
 * @param {Array} data - Array of objects with 'date' property
 * @returns {Array} Array of week arrays, each containing items for that week
 */
export function groupByWeek(data) {
  if (!data || data.length === 0) return []

  // Create a map of week start dates to their items
  const weekMap = new Map()

  data.forEach((item) => {
    const itemDate = typeof item.date === 'string' ? createDate(item.date) : item.date
    const { start: weekStart } = getWeekBounds(itemDate)
    const weekKey = toLocalDateString(weekStart)

    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, {
        weekStart: createDate(weekStart.getTime()),
        items: []
      })
    }
    weekMap.get(weekKey).items.push(item)
  })

  // Sort by week start date and return array of week data
  const sortedWeeks = Array.from(weekMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, weekData]) => weekData)

  return sortedWeeks
}

/**
 * Format a week label from start and end dates
 * @param {Date|string} startDate - Monday of the week
 * @param {Date|string} endDate - Sunday of the week
 * @returns {string} Formatted label, e.g., "6 - 12 Jan 2026" or "30 Dec - 5 Jan 2026"
 */
export function formatWeekLabel(startDate, endDate) {
  const start = typeof startDate === 'string' ? createDate(startDate) : startDate
  const end = typeof endDate === 'string' ? createDate(endDate) : endDate

  const startDay = start.getDate()
  const endDay = end.getDate()
  const startMonth = start.toLocaleDateString('en-GB', { month: 'short' })
  const endMonth = end.toLocaleDateString('en-GB', { month: 'short' })
  const year = end.getFullYear()

  // If same month, format as "6 - 12 Jan 2026"
  if (start.getMonth() === end.getMonth()) {
    return `${startDay} - ${endDay} ${endMonth} ${year}`
  }

  // If different months (spans month boundary), format as "30 Dec - 5 Jan 2026"
  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`
}

/**
 * Get the default yield spread curve for distribution of harvest across flush days
 * Based on typical oyster mushroom flush patterns:
 * - Day 0 (harvest start): 10% of total yield
 * - Day 1: 25% of total yield
 * - Day 2: 40% of total yield (peak)
 * - Day 3: 20% of total yield
 * - Day 4: 5% of total yield
 *
 * @returns {Object} Map of day offset to percentage (0-1)
 */
export function getDefaultYieldCurve() {
  return {
    0: 0.10,
    1: 0.25,
    2: 0.40,
    3: 0.20,
    4: 0.05
  }
}

/**
 * Apply a yield curve to spread an expected yield across multiple days
 * @param {number} totalYieldKg - Total expected yield in kg
 * @param {Date|string} harvestStartDate - Date when harvest begins
 * @param {Object} yieldCurve - Map of day offset to percentage
 * @returns {Array} Array of { date: string (YYYY-MM-DD), yieldKg: number }
 */
export function applyYieldCurve(totalYieldKg, harvestStartDate, yieldCurve = getDefaultYieldCurve()) {
  const startDate = typeof harvestStartDate === 'string'
    ? createDate(harvestStartDate)
    : createDate(harvestStartDate.getTime())

  const distribution = []

  Object.entries(yieldCurve).forEach(([dayOffset, percentage]) => {
    const date = createDate(startDate.getTime())
    date.setDate(date.getDate() + parseInt(dayOffset, 10))

    distribution.push({
      date: toLocalDateString(date),
      yieldKg: totalYieldKg * percentage
    })
  })

  return distribution
}

/**
 * Generate an array of dates between start and end (inclusive)
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {Array} Array of date strings in YYYY-MM-DD format
 */
export function getDateRange(startDate, endDate) {
  const start = typeof startDate === 'string' ? createDate(startDate) : createDate(startDate.getTime())
  const end = typeof endDate === 'string' ? createDate(endDate) : createDate(endDate.getTime())

  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)

  const dates = []
  const current = createDate(start.getTime())

  while (current <= end) {
    dates.push(formatDateISO(current))
    current.setDate(current.getDate() + 1)
  }

  return dates
}

/**
 * Check if a date is in the past (before today)
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is before today
 */
export function isDateInPast(date) {
  const d = typeof date === 'string' ? createDate(date) : createDate(date.getTime())
  const today = createDate()

  d.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)

  return d < today
}

/**
 * Check if a date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is today
 */
export function isDateToday(date) {
  const d = typeof date === 'string' ? createDate(date) : createDate(date.getTime())
  const today = createDate()

  d.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)

  return d.getTime() === today.getTime()
}

/**
 * Format a date as YYYY-MM-DD string
 * @param {Date} date - Date object
 * @returns {string} Date in YYYY-MM-DD format
 */
export function formatDateISO(date) {
  // Use local date components to avoid UTC timezone shift
  // (toISOString() converts to UTC which shifts dates back in BST/other TZs)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
